import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import ooeConstants from '../constants';
import { Order, OrderDeliveryAddress } from '../order/schemas';
import { logDatadogError } from '../services/datadog';
import type { FavoriteOrder } from '../types/order';
import {
  addToFavorites,
  getFavoriteOrders,
  getMemberSearchPageNumber,
  getPastDeliveryAddresses,
  getPastOrders,
  getVaultedCards,
  initializeGuestAccount,
  removeFromFavorites,
  searchForMembers,
  searchGuestByPhone,
  searchGuestsByEmail,
  searchGuestsByName,
  updateFavoriteName,
  validateCardZipcode,
} from './thunks';
import { filterAllowedCards, generateId, isAllowedCard, validateGuest } from './utils';

export type GuestFormValues = {
  cfaId?: string;
  first: string;
  last: string;
  email: string;
  phone: string;
};

export type GuestFormErrors = ReturnType<typeof validateGuest>;

export type GuestFormTouched = {
  first: boolean;
  last: boolean;
  email: boolean;
  phone: boolean;
};

export type State = {
  form: {
    values: GuestFormValues;
    errors: GuestFormErrors;
  };
  loading: {
    nameSearch: boolean;
    emailSearch: boolean;
    phoneSearch: boolean;
    memberSearch: boolean;
    memberSearchMore: boolean;
    guestSession: boolean;
    pastOrders: boolean;
    getFavoriteOrders: boolean;
    vaultedCards: boolean;
    validateZip: boolean;
    addToFavorites: boolean;
    removeFromFavorites: boolean;
    updateFavoriteName: boolean;
    getPastDeliveryAddresses: boolean;
  };
  error: {
    nameSearch: Nullable<string>;
    emailSearch: Nullable<string>;
    phoneSearch: Nullable<string>;
    memberSearch: Nullable<string>;
    guestSession: Nullable<string>;
    vaultedCards: Nullable<string>;
    validateZip: Nullable<string>;
    pastOrders: Nullable<string>;
    getFavoriteOrders: Nullable<string>;
    addToFavorites: Nullable<string>;
    removeFromFavorites: Nullable<string>;
    updateFavoriteName: Nullable<string>;
    getPastDeliveryAddresses: Nullable<string>;
  };
  searchResults: {
    nameSearch: Array<GuestSearchResult>;
    emailSearch: Array<GuestSearchResult>;
    phoneSearch: Array<GuestSearchResult>;
  };
  cmtCustomer: CmtCustomer;
  vaultedCards: Card[] | 'error';
  selectedCard: Card | 'error';
  favoriteOrders: FavoriteOrder[];
  pastDeliveryAddresses: OrderDeliveryAddress[];
  emptyCfaPhoneField: boolean;
  pastOrders: Order[];
  noMorePastOrdersToLoad?: boolean;
  favoriteActionSuccessful?: boolean;
  noPastDeliveryAddresses?: boolean;
  memberSearchValue: string;
  memberSearchResults: MemberSearchMember[];
  memberSearchOpen: boolean;
  // CIS/Rex pages are 1-based. 0 is the proto3 int32 default for next_page and
  // means "no further page" — never a page number we request.
  memberSearchNextPage: number;
  // requestId of the most recently dispatched first-page search. A slower, superseded
  // search (e.g. "test" still in flight when "tester" fires) can land after this and
  // must be ignored rather than overwriting the newer results.
  memberSearchRequestId: Nullable<string>;
};

const initialFormValues: GuestFormValues = {
  first: '',
  last: '',
  email: '',
  phone: '',
};

const initialFormState: State['form'] = {
  values: initialFormValues,
  errors: validateGuest(initialFormValues),
};

export const EMPTY_CMT_CUSTOMER: CmtCustomer = { cfaId: '', isMember: false };

const initialState: State = {
  form: initialFormState,
  error: {
    nameSearch: null,
    emailSearch: null,
    phoneSearch: null,
    memberSearch: null,
    guestSession: null,
    vaultedCards: null,
    validateZip: null,
    pastOrders: null,
    getFavoriteOrders: null,
    addToFavorites: null,
    removeFromFavorites: null,
    updateFavoriteName: null,
    getPastDeliveryAddresses: null,
  },
  loading: {
    nameSearch: false,
    emailSearch: false,
    phoneSearch: false,
    memberSearch: false,
    memberSearchMore: false,
    guestSession: false,
    pastOrders: false,
    getFavoriteOrders: false,
    vaultedCards: false,
    validateZip: false,
    addToFavorites: false,
    removeFromFavorites: false,
    updateFavoriteName: false,
    getPastDeliveryAddresses: false,
  },
  searchResults: {
    nameSearch: [],
    emailSearch: [],
    phoneSearch: [],
  },
  cmtCustomer: EMPTY_CMT_CUSTOMER,
  vaultedCards: [],
  selectedCard: {} as Card,
  favoriteOrders: [],
  pastDeliveryAddresses: [],
  pastOrders: [],
  emptyCfaPhoneField: false,
  memberSearchValue: '',
  memberSearchResults: [],
  memberSearchOpen: false,
  memberSearchNextPage: 0,
  memberSearchRequestId: null,
};

const slice = createSlice({
  name: 'guest',
  initialState,
  reducers: {
    setGuestFormField: (state, action: PayloadAction<{ field: keyof GuestFormTouched; value: string }>) => {
      const { field, value } = action.payload;
      state.form.values[field] = value;
      state.form.errors = validateGuest(state.form.values);
    },
    setRawFormField: (state, action: PayloadAction<{ field: keyof GuestFormValues; value: string }>) => {
      const { field, value } = action.payload;
      state.form.values[field] = value;
    },
    setMemberSearchValue: (state, action: PayloadAction<{ value: string }>) => {
      const { value } = action.payload;
      state.memberSearchValue = value;
      // Paging is per-query. Drop it immediately so the infinite-scroll sentinel can't
      // append a page of the old query's results while the new search is still debouncing.
      state.memberSearchNextPage = 0;
      state.loading.memberSearchMore = false;

      if (value.trim().length >= ooeConstants.MEMBER_SEARCH_MIN_TYPEAHEAD_LENGTH) {
        return;
      }

      state.memberSearchResults = [];
      state.memberSearchOpen = false;
      state.error.memberSearch = null;
    },
    closeMemberSearch: (state) => {
      state.memberSearchOpen = false;
      state.memberSearchResults = [];
      state.memberSearchNextPage = 0;
      state.loading.memberSearchMore = false;
      state.error.memberSearch = null;
    },
    validateFormField: (state, action: PayloadAction<{ field: keyof GuestFormErrors }>) => {
      const { field } = action.payload;
      const fieldError = validateGuest(state.form.values)[field];
      // validateGuest() omits the key entirely for a valid field -- delete it here too,
      // rather than assigning undefined, so a stale key doesn't linger and get counted as
      // an error by consumers that only check for key presence (e.g.
      // selectGuestFormSyncErrors).
      if (fieldError) {
        state.form.errors[field] = fieldError;
      } else {
        delete state.form.errors[field];
      }
    },
    initializeGuestForm: (state, action: PayloadAction<Partial<GuestFormValues>>) => {
      state.form.values = { ...initialFormValues, ...action.payload };
      state.form.errors = validateGuest(state.form.values);
    },
    resetGuestForm: (state) => {
      state.form = initialFormState;
    },
    refreshExpiredSessionToken: () => {},
    setCMTCustomer: (state, action: PayloadAction<CmtCustomer>) => {
      const cmtCustomer = action.payload;
      const phone = cmtCustomer.phone ?? '';
      const formValues: GuestFormValues = {
        cfaId: cmtCustomer.cfaId,
        first: cmtCustomer.first ?? cmtCustomer.firstName ?? '',
        last: cmtCustomer.last ?? cmtCustomer.lastName ?? '',
        email: cmtCustomer.email ?? '',
        phone,
      };

      state.searchResults = { nameSearch: [], emailSearch: [], phoneSearch: [] };
      state.vaultedCards = [];
      state.selectedCard = {} as Card;
      state.pastOrders = [];
      state.favoriteOrders = [];
      state.pastDeliveryAddresses = [];
      state.noPastDeliveryAddresses = false;
      state.emptyCfaPhoneField = !!cmtCustomer.cfaId && phone === '';
      state.cmtCustomer = cmtCustomer.cfaId
        ? { ...cmtCustomer, originalPhone: cmtCustomer.originalPhone ?? phone }
        : EMPTY_CMT_CUSTOMER;
      state.form = { values: formValues, errors: validateGuest(formValues) };

      if (!cmtCustomer.cfaId) {
        state.memberSearchResults = [];
        state.memberSearchOpen = false;
        state.memberSearchNextPage = 0;
        state.loading.memberSearchMore = false;
        state.noMorePastOrdersToLoad = false;
        state.error.getFavoriteOrders = null;
        state.error.pastOrders = null;
        return;
      }

      state.loading.guestSession = true;
      state.loading.pastOrders = true;
      state.loading.getFavoriteOrders = true;
    },
    getOrderAccountSuccess: (state, action: PayloadAction<{ account: Card | PaymentOption }>) => {
      const { account } = action.payload;
      if (!isAllowedCard(account)) {
        return;
      }

      const generatedId = generateId(account);
      const selectedCard = { ...account, generatedId, validated: true };
      state.selectedCard = selectedCard;
      if (state.vaultedCards === 'error') {
        return;
      }

      const foundCardIndex = state.vaultedCards.findIndex((card) => card.generatedId === generatedId);
      if (foundCardIndex >= 0) {
        state.vaultedCards[foundCardIndex] = selectedCard;
      } else {
        state.vaultedCards.push(selectedCard);
      }
    },
    closeEmptyCfaPhoneFieldWarning: (state) => {
      state.emptyCfaPhoneField = false;
    },
    clearFavoriteErrors: (state) => {
      state.favoriteActionSuccessful = false;
      state.error.removeFromFavorites = null;
      state.error.addToFavorites = null;
      state.error.updateFavoriteName = null;
    },
    clearValidateZipError: (state) => {
      state.error.validateZip = null;
    },
    resetState: () => {
      return initialState;
    },
    queueSearchGuestsByName: () => {},
    queueSearchGuestsByEmail: () => {},
    queueSearchGuestsByPhone: () => {},
    submitMemberSearch: () => {},
    overrideGuestDetails: () => {},
    retrieveGuestAdditionalInformation: (state) => {
      state.vaultedCards = [];
      state.selectedCard = {} as Card;
      state.loading.guestSession = false;
      state.loading.vaultedCards = true;
      state.error.guestSession = null;
    },
    loadMorePastOrders: () => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchGuestsByName.pending, (state) => {
        state.loading.nameSearch = true;
        state.error.nameSearch = null;
      })
      .addCase(searchGuestsByName.fulfilled, (state, action) => {
        const { searchResults } = action.payload;
        state.searchResults.nameSearch = searchResults;
        state.loading.nameSearch = false;
        state.error.nameSearch = null;
      })
      .addCase(searchGuestsByName.rejected, (state, action) => {
        logDatadogError('Search Guests By Name Error', { guest: state }, action.error);
        state.searchResults.nameSearch = [];
        state.error.nameSearch = 'Error: Name Search';
        state.loading.nameSearch = false;
      })
      .addCase(searchGuestsByEmail.pending, (state) => {
        state.loading.emailSearch = true;
        state.error.emailSearch = null;
      })
      .addCase(searchGuestsByEmail.fulfilled, (state, action) => {
        const { searchResults } = action.payload;
        state.searchResults.emailSearch = searchResults;
        state.loading.emailSearch = false;
        state.error.emailSearch = null;
      })
      .addCase(searchGuestsByEmail.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Search Guests By Email Error', { guest: state }, error);
        const errorMessage = error.message;
        state.searchResults.emailSearch = [];
        state.error.emailSearch =
          errorMessage === 'Error' ? `${errorMessage}: Email Search` : (errorMessage ?? null);
        state.loading.emailSearch = false;
      })
      .addCase(searchGuestByPhone.pending, (state) => {
        state.loading.phoneSearch = true;
        state.error.phoneSearch = null;
      })
      .addCase(searchGuestByPhone.fulfilled, (state, action) => {
        const { searchResults } = action.payload;
        state.searchResults.phoneSearch = searchResults;
        state.loading.phoneSearch = false;
        state.error.phoneSearch = null;
      })
      .addCase(searchGuestByPhone.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Search Guest By Phone Error', { guest: state }, error);
        const errorMessage = error.message;
        state.searchResults.phoneSearch = [];
        state.error.phoneSearch =
          errorMessage === 'Error' ? `${errorMessage}: Phone Search` : (errorMessage ?? null);
        state.loading.phoneSearch = false;
      })
      .addCase(searchForMembers.pending, (state, action) => {
        const isFirstPage =
          getMemberSearchPageNumber(action.meta.arg) <= ooeConstants.FIRST_MEMBER_SEARCH_PAGE;
        state.error.memberSearch = null;
        state.memberSearchOpen = true;
        if (isFirstPage) {
          // Always the newest first-page dispatch, even if an older one is still in
          // flight -- that's what fulfilled/rejected below compare against to drop stale
          // responses.
          state.memberSearchRequestId = action.meta.requestId;
          state.loading.memberSearch = true;
          state.loading.memberSearchMore = false;
          state.memberSearchResults = [];
          state.memberSearchNextPage = 0;
          return;
        }
        state.loading.memberSearchMore = true;
      })
      .addCase(searchForMembers.fulfilled, (state, action) => {
        const isFirstPage =
          getMemberSearchPageNumber(action.meta.arg) <= ooeConstants.FIRST_MEMBER_SEARCH_PAGE;
        if (isFirstPage && action.meta.requestId !== state.memberSearchRequestId) {
          // A slower search superseded by a newer one -- drop it instead of overwriting
          // the newer results.
          return;
        }
        const { searchResults, nextPage } = action.payload;
        const currentResults = isFirstPage ? [] : state.memberSearchResults;
        const existingIds = new Set(currentResults.map((member) => member.id));
        state.memberSearchResults = currentResults.concat(
          searchResults.filter((member) => !existingIds.has(member.id)),
        );
        state.memberSearchNextPage = nextPage;
        state.loading.memberSearch = false;
        state.loading.memberSearchMore = false;
        state.error.memberSearch = null;
        state.memberSearchOpen = true;
      })
      .addCase(searchForMembers.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        const isFirstPage =
          getMemberSearchPageNumber(action.meta.arg) <= ooeConstants.FIRST_MEMBER_SEARCH_PAGE;
        const { error } = action;
        logDatadogError('Search For Members Error', { guest: state }, error);
        const errorMessage = error.message;
        if (isFirstPage && action.meta.requestId !== state.memberSearchRequestId) {
          // A slower search superseded by a newer one -- drop it instead of clobbering
          // the newer results with this one's timeout/error.
          return;
        }
        state.loading.memberSearch = false;
        state.loading.memberSearchMore = false;
        state.memberSearchOpen = true;
        if (!isFirstPage) {
          return;
        }
        state.memberSearchResults = [];
        state.memberSearchNextPage = 0;
        state.error.memberSearch =
          errorMessage === 'Error' ? `${errorMessage}: Member Search` : (errorMessage ?? null);
      })
      .addCase(getVaultedCards.fulfilled, (state, action) => {
        const { vaultedCards } = action.payload;
        let vaultedCardsWithId: Card[] = [];
        if (vaultedCards) {
          const allowedCards = filterAllowedCards(vaultedCards);
          vaultedCardsWithId = allowedCards.map((card) => ({
            ...card,
            generatedId: generateId(card),
            validated: false,
          }));
        }
        state.vaultedCards = vaultedCardsWithId;
        state.error.vaultedCards = null;
        state.loading.vaultedCards = false;
      })
      .addCase(getVaultedCards.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Vaulted Cards Error', { guest: state }, error);
        state.vaultedCards = 'error';
        state.error.vaultedCards = error.message ?? null;
        state.loading.vaultedCards = false;
      })
      .addCase(validateCardZipcode.pending, (state) => {
        state.loading.validateZip = true;
      })
      .addCase(validateCardZipcode.fulfilled, (state, action) => {
        const { cards } = action.payload;

        state.loading.validateZip = false;

        if (cards.length === 0) {
          state.selectedCard = 'error';
          state.error.validateZip = 'Card ZIP code is invalid';
          return;
        }

        state.error.validateZip = null;

        const selectedCard = cards[0];
        const generatedId = generateId(selectedCard);
        const vaultedCard = { ...selectedCard, generatedId, validated: true };
        state.selectedCard = vaultedCard;
        if (Array.isArray(state.vaultedCards)) {
          const index = state.vaultedCards.findIndex((card) => card.generatedId === generatedId);
          if (index >= 0) {
            state.vaultedCards[index] = vaultedCard;
          }
        }
      })
      .addCase(validateCardZipcode.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Validate Card Zipcode Error', { guest: state }, error);
        state.selectedCard = 'error';
        state.error.validateZip = error.message ?? null;
        state.loading.validateZip = false;
      })
      .addCase(getPastOrders.pending, (state) => {
        state.loading.pastOrders = true;
        state.error.pastOrders = null;
      })
      .addCase(getPastOrders.fulfilled, (state, action) => {
        const { pastOrders } = action.payload;
        const { numToLoad } = action.meta.arg;
        state.pastOrders = pastOrders;
        state.noMorePastOrdersToLoad = pastOrders.length < numToLoad;
        state.loading.pastOrders = false;
        state.error.pastOrders = null;
      })
      .addCase(getPastOrders.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Past Orders Error', { guest: state }, error);
        state.error.pastOrders = error.message ?? null;
        state.loading.pastOrders = false;
      })
      .addCase(getPastDeliveryAddresses.pending, (state) => {
        state.loading.getPastDeliveryAddresses = true;
        state.error.getPastDeliveryAddresses = null;
      })
      .addCase(getPastDeliveryAddresses.fulfilled, (state, action) => {
        const { addresses } = action.payload;
        state.loading.getPastDeliveryAddresses = false;

        if (addresses.length < 1) {
          state.noPastDeliveryAddresses = true;
        } else {
          state.pastDeliveryAddresses = addresses;
        }
      })
      .addCase(getPastDeliveryAddresses.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Past Delivery Addresses Error', { guest: state }, error);
        state.error.getPastDeliveryAddresses = error.message ?? null;
        state.loading.getPastDeliveryAddresses = false;
      })
      .addCase(getFavoriteOrders.pending, (state) => {
        state.loading.getFavoriteOrders = true;
        state.error.getFavoriteOrders = null;
      })
      .addCase(getFavoriteOrders.fulfilled, (state, action) => {
        const { favoriteOrders } = action.payload;
        state.favoriteOrders = favoriteOrders;
        state.loading.getFavoriteOrders = false;
        state.error.getFavoriteOrders = null;
      })
      .addCase(getFavoriteOrders.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Favorite Orders Error', { guest: state }, error);
        state.error.getFavoriteOrders = error.message ?? null;
        state.loading.getFavoriteOrders = false;
      })
      .addCase(addToFavorites.pending, (state) => {
        state.loading.addToFavorites = true;
        state.error.addToFavorites = null;
      })
      .addCase(addToFavorites.fulfilled, (state) => {
        state.favoriteActionSuccessful = true;
        state.loading.addToFavorites = false;
        state.error.addToFavorites = null;
      })
      .addCase(addToFavorites.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Add To Favorites Error', { guest: state }, error);
        state.error.addToFavorites = error.message ?? null;
        state.loading.addToFavorites = false;
      })
      .addCase(removeFromFavorites.pending, (state) => {
        state.loading.removeFromFavorites = true;
        state.error.removeFromFavorites = null;
      })
      .addCase(removeFromFavorites.fulfilled, (state) => {
        state.favoriteActionSuccessful = true;
        state.loading.removeFromFavorites = false;
        state.error.removeFromFavorites = null;
      })
      .addCase(removeFromFavorites.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Remove From Favorites Error', { guest: state }, error);
        state.error.removeFromFavorites = error.message ?? null;
        state.loading.removeFromFavorites = false;
      })
      .addCase(updateFavoriteName.pending, (state) => {
        state.loading.updateFavoriteName = true;
        state.error.updateFavoriteName = null;
      })
      .addCase(updateFavoriteName.fulfilled, (state) => {
        state.favoriteActionSuccessful = true;
        state.loading.updateFavoriteName = false;
        state.error.updateFavoriteName = null;
      })
      .addCase(updateFavoriteName.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Update Favorite Name Error', { guest: state }, error);
        state.error.updateFavoriteName = error.message ?? null;
        state.loading.updateFavoriteName = false;
      })
      .addCase(initializeGuestAccount.fulfilled, (state, action) => {
        const { cfaId } = action.payload;
        state.cmtCustomer = { cfaId, isMember: false };
        state.loading.guestSession = false;
        state.error.guestSession = null;
      })
      .addCase(initializeGuestAccount.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Initialize Guest Account Error', { guest: state }, error);
        state.error.guestSession = error.message ?? null;
        state.loading.guestSession = false;
      });
  },
});

export const actions = slice.actions;
export default slice.reducer;
